import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner, Alert, Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { FaCloudDownloadAlt, FaSync, FaCheckCircle, FaSearch, FaExclamationTriangle } from "react-icons/fa";
import cargaComensalesLogiflexApi from "../../../services/api/cargaComensalesLogiflexApi";
import clienteApi from "../../../services/api/clienteApi";
import { reporteApi } from "../../../services/api/reporteApi";
import "react-toastify/dist/ReactToastify.css";

const preferirMantener = (duplicados) => {
  if (!duplicados?.length) return null;
  const activo = duplicados.find((d) => d.activo);
  const pool = activo ? [activo] : duplicados;
  return pool.reduce((a, b) =>
    (a.cantidad_servicios || a.cantidad_atenciones || 0) >=
    (b.cantidad_servicios || b.cantidad_atenciones || 0)
      ? a
      : b
  ).comensal_id;
};

const ESTADOS = [
  { value: "", label: "Todos" },
  { value: "NUEVO", label: "Novedades (altas)" },
  { value: "ACTUALIZAR", label: "Novedades (cambios)" },
  { value: "SIN_CAMBIOS", label: "Ya cargados" },
  { value: "ERROR", label: "Errores" },
];

const badgeVariant = (estado) => {
  switch (estado) {
    case "NUEVO":
      return "info";
    case "ACTUALIZAR":
      return "warning";
    case "SIN_CAMBIOS":
      return "success";
    case "ERROR":
      return "danger";
    default:
      return "secondary";
  }
};

const extraerError = (error, fallback) => {
  const msg = error.response?.data?.detail || error.response?.data?.message || fallback;
  return typeof msg === "string" ? msg : JSON.stringify(msg);
};

const CargarComensalesLogiflex = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");
  const proyectoId = selectedProject?.id;

  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [programacion, setProgramacion] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [clientes, setClientes] = useState([]);
  const [idCliente, setIdCliente] = useState("");
  const [duplicadosFila, setDuplicadosFila] = useState(null);
  const [idMantener, setIdMantener] = useState(null);
  const [confirmConsolidar, setConfirmConsolidar] = useState(false);
  const [consolidando, setConsolidando] = useState(false);

  const cargarAuxiliares = useCallback(async () => {
    if (!proyectoId) return;
    try {
      const [cliRes, histRes, progRes] = await Promise.all([
        clienteApi.getByProyecto(proyectoId),
        cargaComensalesLogiflexApi.historial(proyectoId).catch(() => ({ data: { data: [] } })),
        cargaComensalesLogiflexApi.programacion().catch(() => ({ data: { data: null } })),
      ]);
      const list = cliRes.data?.data || cliRes.data || [];
      const clientesList = Array.isArray(list) ? list : [];
      setClientes(clientesList);
      setIdCliente((prev) => prev || (clientesList.length === 1 ? String(clientesList[0].id) : ""));
      setHistorial(histRes.data?.data || []);
      setProgramacion(progRes.data?.data || null);
    } catch (error) {
      toast.error(extraerError(error, "No se pudieron cargar datos auxiliares"));
    }
  }, [proyectoId]);

  useEffect(() => {
    cargarAuxiliares();
  }, [cargarAuxiliares]);

  useEffect(() => {
    if (!duplicadosFila) {
      setIdMantener(null);
      setConfirmConsolidar(false);
      return;
    }
    setIdMantener(
      duplicadosFila.id_comensal_sugerido || preferirMantener(duplicadosFila.duplicados)
    );
    setConfirmConsolidar(false);
  }, [duplicadosFila]);

  const ejecutar = async (aplicar, opciones = {}) => {
    if (!proyectoId) {
      toast.error("Seleccione un proyecto");
      return;
    }
    if (aplicar) setAplicando(true);
    else setLoading(true);
    try {
      const { data } = await cargaComensalesLogiflexApi.ejecutar(proyectoId, {
        aplicar,
        id_cliente: idCliente ? Number(idCliente) : null,
      });
      setResultado(data);
      setProgramacion(data.programacion || programacion);
      if (!opciones.silencioso) {
        toast.success(data.message || (aplicar ? "Carga aplicada" : "Vista previa lista"));
      }
      const hist = await cargaComensalesLogiflexApi.historial(proyectoId).catch(() => null);
      if (hist?.data?.data) setHistorial(hist.data.data);
    } catch (error) {
      toast.error(extraerError(error, "Error al consultar empleados Logiflex"));
    } finally {
      setLoading(false);
      setAplicando(false);
    }
  };

  const idsEliminar = (duplicadosFila?.duplicados || [])
    .map((d) => d.comensal_id)
    .filter((id) => id !== idMantener);

  const solicitarConsolidar = () => {
    if (!duplicadosFila?.puede_consolidar) return;
    if (!idMantener) {
      toast.warning("Seleccione el comensal que desea conservar.");
      return;
    }
    if (idsEliminar.length === 0) {
      toast.warning("Debe haber al menos un comensal para eliminar.");
      return;
    }
    setConfirmConsolidar(true);
  };

  const ejecutarConsolidar = async () => {
    if (!duplicadosFila || !idMantener) return;
    setConsolidando(true);
    setConfirmConsolidar(false);
    try {
      const res = await reporteApi.consolidarComensalesDuplicados({
        id_comensal_mantener: idMantener,
        id_comensales_eliminar: idsEliminar,
        criterio: "documento",
      });
      if (res.data?.success) {
        const stats = res.data.estadisticas || {};
        const extra =
          stats.turnos_detalle_actualizados != null
            ? ` Se reasignaron ${stats.turnos_detalle_actualizados} servicio(s) en detalle de turnos.`
            : "";
        toast.success((res.data.message || "Consolidación completada.") + extra);
        setDuplicadosFila(null);
        await ejecutar(false, { silencioso: true });
      } else {
        toast.error(res.data?.message || "No se pudo consolidar.");
      }
    } catch (error) {
      toast.error(extraerError(error, "Error al consolidar comensales"));
    } finally {
      setConsolidando(false);
    }
  };

  const filas = resultado?.filas || [];
  const resumen = resultado?.resumen;
  const filasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas.filter((f) => {
      if (filtroEstado && f.estado !== filtroEstado) return false;
      if (!q) return true;
      return (
        String(f.dni || "").includes(q) ||
        String(f.nombre_logiflex || "").toLowerCase().includes(q) ||
        String(f.nombre_sgc || "").toLowerCase().includes(q)
      );
    });
  }, [filas, filtroEstado, busqueda]);

  const novedades = (resumen?.nuevos || 0) + (resumen?.actualizados || 0);

  return (
    <div className="dh-reporte-bg">
      <style>
        {`
          .dh-reporte-bg { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); min-height: 100vh; padding-bottom: 2rem; }
          .dh-main-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 2rem 0; margin-bottom: 2rem; border-radius: 0 0 30px 30px; box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2); }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card { border: none; border-radius: 20px; box-shadow: 0 15px 35px rgba(0,0,0,0.1); background: white; }
          .dh-card-header { border-radius: 20px 20px 0 0; padding: 1.25rem 1.5rem; border: none; font-weight: 700; }
          .dh-card-header-primary { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; }
          .dh-card-header-success { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); color: white; }
          .dh-card-header-info { background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%); color: white; }
          .btn-dh-primary { background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%); border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600; }
          .btn-dh-primary:hover { color: white; opacity: 0.95; }
          .btn-dh-secondary { background: linear-gradient(135deg, #6b7280 0%, #374151 100%); border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600; }
          .btn-dh-secondary:hover { color: white; opacity: 0.95; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control { border-radius: 10px; }
          .dh-alert-info { background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%); border: 1px solid #93c5fd; border-radius: 12px; padding: 1rem 1.25rem; color: #1e40af; }
          .stat-box { text-align: center; padding: 0.75rem; border-radius: 12px; background: #f8fafc; }
          .stat-box strong { display: block; font-size: 1.4rem; }
        `}
      </style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaCloudDownloadAlt className="me-2" />
            Cargar comensales Logiflex
          </h1>
          <p className="dh-main-subtitle">
            En Logiflex son empleados activos; en SGC se cargan como comensales.
            Solo novedades: altas nuevas o cambios de nombre, cargo o empresa.
          </p>
        </Container>
      </div>

      <Container>
        {!proyectoId ? (
          <Alert className="dh-alert dh-alert-info">Seleccione un proyecto para continuar.</Alert>
        ) : (
          <>
            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header dh-card-header-primary">Consulta y carga</Card.Header>
              <Card.Body>
                <Alert className="dh-alert dh-alert-info mb-3">
                  El job automático corre a las{" "}
                  <strong>{programacion?.hora_pe || "02:00"}</strong> hora Perú
                  {programacion?.job_habilitado ? " (habilitado en el servidor)" : " (aún no habilitado en el servidor)"}.
                  No vuelve a importar comensales que ya existen sin cambios.
                </Alert>
                <Row className="g-3 align-items-end">
                  <Col md={5}>
                    <Form.Label className="dh-form-label">Cliente por defecto (altas)</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={idCliente}
                      onChange={(e) => setIdCliente(e.target.value)}
                    >
                      <option value="">Resolver por empresa Logiflex</option>
                      {clientes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={7} className="d-flex gap-2 flex-wrap">
                    <Button
                      className="btn-dh-secondary"
                      onClick={() => ejecutar(false)}
                      disabled={loading || aplicando}
                    >
                      {loading ? <Spinner size="sm" className="me-2" /> : <FaSearch className="me-2" />}
                      Consultar novedades
                    </Button>
                    <Button
                      className="btn-dh-primary"
                      onClick={() => ejecutar(true)}
                      disabled={loading || aplicando || !resultado}
                    >
                      {aplicando ? <Spinner size="sm" className="me-2" /> : <FaCheckCircle className="me-2" />}
                      Cargar novedades{novedades ? ` (${novedades})` : ""}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {resumen && (
              <Row className="g-3 mb-4">
                {[
                  ["Logiflex", resumen.total_logiflex],
                  ["Altas", resumen.nuevos],
                  ["Cambios", resumen.actualizados],
                  ["Sin cambios", resumen.sin_cambios],
                  ["Errores", resumen.errores],
                ].map(([label, value]) => (
                  <Col key={label} xs={6} md>
                    <div className="stat-box">
                      <strong>{value ?? 0}</strong>
                      <span className="text-muted">{label}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header dh-card-header-success">Resultados</Card.Header>
              <Card.Body>
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <Form.Select
                      className="dh-form-control"
                      value={filtroEstado}
                      onChange={(e) => setFiltroEstado(e.target.value)}
                    >
                      {ESTADOS.map((e) => (
                        <option key={e.value} value={e.value}>
                          {e.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={5}>
                    <Form.Control
                      className="dh-form-control"
                      placeholder="Buscar DNI o nombre"
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Button className="btn-dh-secondary w-100" onClick={() => ejecutar(false)} disabled={loading}>
                      <FaSync className="me-2" />
                      Refrescar
                    </Button>
                  </Col>
                </Row>
                {!filas.length ? (
                  <p className="text-muted mb-0">Consulte Logiflex para ver las novedades de comensales.</p>
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm">
                      <thead>
                        <tr>
                          <th>Estado</th>
                          <th>DNI</th>
                          <th>Logiflex</th>
                          <th>SGC</th>
                          <th>Empresa</th>
                          <th>Cargo</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filasFiltradas.map((f) => (
                          <tr key={`${f.estado}-${f.dni}-${f.comensal_id || ""}`}>
                            <td>
                              <Badge bg={badgeVariant(f.estado)}>{f.estado}</Badge>
                            </td>
                            <td>{f.dni}</td>
                            <td>{f.nombre_logiflex || "—"}</td>
                            <td>{f.nombre_sgc || "—"}</td>
                            <td>{f.empresa_logiflex || "—"}</td>
                            <td>{f.cargo_logiflex || "—"}</td>
                            <td>
                              {Array.isArray(f.duplicados) && f.duplicados.length > 1 ? (
                                <Button
                                  variant="link"
                                  className="p-0 text-danger fw-semibold text-start"
                                  onClick={() => setDuplicadosFila(f)}
                                >
                                  {f.motivo}
                                </Button>
                              ) : (
                                f.motivo || "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="dh-card">
              <Card.Header className="dh-card-header dh-card-header-info">Historial de cargas</Card.Header>
              <Card.Body>
                {!historial.length ? (
                  <p className="text-muted mb-0">Aún no hay ejecuciones registradas.</p>
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm">
                      <thead>
                        <tr>
                          <th>Fecha PE</th>
                          <th>Origen</th>
                          <th>Modo</th>
                          <th>Altas</th>
                          <th>Cambios</th>
                          <th>Sin cambios</th>
                          <th>Mensaje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historial.map((h) => (
                          <tr key={h.id}>
                            <td>{h.created_at}</td>
                            <td>{h.origen}</td>
                            <td>{h.aplicar ? "Aplicó" : "Preview"}</td>
                            <td>{h.nuevos}</td>
                            <td>{h.actualizados}</td>
                            <td>{h.sin_cambios}</td>
                            <td>{h.mensaje}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Container>
      <Modal
        show={!!duplicadosFila}
        onHide={() => setDuplicadosFila(null)}
        centered
        size="lg"
        contentClassName="border-0 shadow rounded-3 overflow-hidden"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
        >
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaExclamationTriangle />
            Comensales duplicados · DNI {duplicadosFila?.dni || "—"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          <Alert className="dh-alert dh-alert-info mb-3">
            Logiflex: <strong>{duplicadosFila?.nombre_logiflex || "—"}</strong>
            {duplicadosFila?.empresa_logiflex ? ` · ${duplicadosFila.empresa_logiflex}` : ""}.
            En SGC hay más de un comensal con el mismo DNI; no se carga hasta resolver el duplicado.
          </Alert>
          {duplicadosFila?.puede_consolidar ? (
            <p className="small text-muted">
              Seleccione el ID que se conserva. Los servicios de <code>turnos_detalle</code>,
              dietas y comedores del ID eliminado se reasignan al conservado, igual que en
              Consultas → Comensales duplicados.
            </p>
          ) : (
            <Alert variant="warning" className="small">
              {duplicadosFila?.motivo_no_consolidable ||
                "Este duplicado no se puede consolidar desde aquí."}{" "}
              <Button
                variant="link"
                className="p-0 align-baseline"
                onClick={() => navigate("/consultas/alimentacion/comensales-duplicados")}
              >
                Ir a comensales duplicados
              </Button>
            </Alert>
          )}
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  {duplicadosFila?.puede_consolidar ? <th>Conservar</th> : null}
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Doc. identidad</th>
                  <th>Cliente</th>
                  <th className="text-center">Servicios</th>
                  <th>Cargo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {(duplicadosFila?.duplicados || []).map((d) => (
                  <tr key={d.comensal_id}>
                    {duplicadosFila?.puede_consolidar ? (
                      <td className="text-center">
                        <Form.Check
                          type="radio"
                          name="comensal-mantener"
                          checked={idMantener === d.comensal_id}
                          onChange={() => setIdMantener(d.comensal_id)}
                        />
                      </td>
                    ) : null}
                    <td>{d.comensal_id}</td>
                    <td>{d.nombre || "—"}</td>
                    <td>{d.numero_documento_identidad || "—"}</td>
                    <td>{d.nombre_cliente || "—"}</td>
                    <td className="text-center">
                      <Badge bg={(d.cantidad_servicios || 0) > 0 ? "primary" : "secondary"}>
                        {d.cantidad_servicios ?? d.cantidad_atenciones ?? 0}
                      </Badge>
                    </td>
                    <td>{d.cargo || "—"}</td>
                    <td>
                      <Badge bg={d.activo ? "success" : "secondary"}>
                        {d.activo ? "Activo" : "Inactivo"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button className="btn-dh-secondary" onClick={() => setDuplicadosFila(null)}>
            Cerrar
          </Button>
          {duplicadosFila?.puede_consolidar ? (
            <Button
              className="btn-dh-primary"
              onClick={solicitarConsolidar}
              disabled={consolidando || !idMantener}
            >
              {consolidando ? <Spinner size="sm" className="me-2" /> : null}
              Consolidar
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
      <Modal show={confirmConsolidar} onHide={() => setConfirmConsolidar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar consolidación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Conservar <strong>#{idMantener}</strong> y eliminar:{" "}
            <strong>{idsEliminar.map((id) => `#${id}`).join(", ")}</strong>.
          </p>
          <p className="mb-0 small text-muted">
            Criterio: <strong>documento</strong>. Los servicios de detalle de turnos
            (<code>turnos_detalle.id_comensal</code>), dietas y comedores del ID
            eliminado se actualizan al ID conservado antes de borrar el duplicado.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmConsolidar(false)}>
            Cancelar
          </Button>
          <Button className="btn-dh-primary" onClick={ejecutarConsolidar} disabled={consolidando}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default CargarComensalesLogiflex;
